import { ArticleContent } from "@roc-digital/ui-lib";
import { DropEvent } from "./Slot";
import { View, Zone } from './types';
import { ArticleSchema, ZoneSchema } from "@roc-digital/mxm-base/src/data";

export function mutateZoneDropEvent(view: View, event: DropEvent) {
  let next = {...view};

  const fromZone = next.zones.find(z => z.id === event.from.id);
  const toZone = next.zones.find(z => z.id === event.to.id);

  if(!fromZone || !toZone || fromZone.id === toZone.id) {
    return view;
  }

  next.zones.splice(next.zones.indexOf(fromZone), 1);
  next.zones.splice(next.zones.indexOf(toZone), 0, fromZone);

  next.zones.forEach((zone, index) => zone.order = index + 1);
 
  return next;
}

function getEventDetails(view: View, data: any): {
  type: 'zone' | 'bucket' | 'trash',
  article: ArticleSchema,
  zone?: Zone
} | null {
  const details: {
    type: 'zone' | 'bucket' | 'trash',
    article: ArticleSchema,
    zone?: Zone
  } = {} as any;

  if(data.area === 'bucket') {
    details.type = 'bucket';
    details.zone = null as any;
    details.article = data.article;

    return details;
  }

  if(data.area === 'trash') {
    details.type = 'trash';
    details.zone = null as any;
    details.article = null as any;

    return details;
  }

  const zone = view.zones.find(z => z.id === data.area);
  
  if(!zone) {
    return null as any;
  }

  const article = zone.articles.find(a => a.id === data.articleId);

  details.type = 'zone';
  details.zone = zone;
  details.article = article as any;

  return details;
}

export function isStaticArticle(zone: Zone | undefined, articleId: string | undefined) {
  if(!zone || !articleId) return;
  return (zone?.articleIds || []).includes(articleId);
}

function insertArticleInZone(zone: Zone | undefined, article: ArticleSchema, index: number | undefined) {
  if(!zone || index === undefined) return;
  if(isStaticArticle(zone, article?.id)) return;  // article already in zone;
  if(article.id.startsWith('temp_')) return;  // skip placeholder articles;
  
  if(!zone.articleIds) zone.articleIds = []
  zone.articleIds.push(article.id);
  const articleAtIndex = zone.articles[index];
  if(articleAtIndex?.id && articleAtIndex.id.startsWith('temp_')) {
    zone.articles.splice(index, 1, article);
  } else {
    zone.articles.splice(index, 0, article);
  }
}

function removeArticleFromZone(zone: Zone | undefined, article: ArticleSchema) {
  if(!zone) return;
  if(isStaticArticle(zone, article?.id) === false) return;  // article not in zone;
  if(article.id.startsWith('temp_')) return;  // skip placeholder articles;
  if(!zone.articleIds) zone.articleIds = []
  zone.articleIds = zone.articleIds.filter(id => id !== article.id);
  const index = zone.articles.findIndex(a => a.id === article.id);
  if(index > -1) {
    zone.articles.splice(index, 1);
  }
}

export function mutateViewForDropEvent(view: View, event: DropEvent, articles: {[id: string]: ArticleSchema}) {

  const from = getEventDetails(view,event.from);
  const to = getEventDetails(view, event.to);

  if(from?.type === 'trash' || to?.type === 'bucket') {
    return view;
  }

  if(from?.zone && from?.type === 'zone' && to?.type === 'zone' && to?.zone?.id === from?.zone?.id) {
    // Switch
    const articles = from.zone?.articles || [];

    if(articles[event.from.slot] && articles[event.to.slot]) {
      const fromArticle = articles[event.from.slot];
      const toArticle = articles[event.to.slot];
      articles[event.to.slot] = fromArticle;
      articles[event.from.slot] = toArticle;
      from.zone.articles = [...articles];
    }
  } else if(from?.type === 'zone' && to?.type === 'zone') {
    removeArticleFromZone(from.zone, from.article);                                          // Remove article
    insertArticleInZone(to.zone, from.article, to.zone?.articles.indexOf(to.article));       // Add Article
  } else if(from?.type === 'bucket' && to) {
    insertArticleInZone(to.zone, from.article, event.to.slot);     
  } else if(to?.type === 'trash' && from) {
    // Delete
    removeArticleFromZone(from.zone, from.article);                                          // Remove article
  }

  return clampMaxArticles(view);
}

export function extractZoneArticles(zone: Zone, articles: ArticleContent[]) {
  return [
    ...(zone.articles || []).map(article => articles.find(a => a.id === article.id) || {id: 'temp_' + Math.random().toString()} as any),
    {id: 'temp_' + Math.random().toString()} ,{id: 'temp_' + Math.random().toString()}, {id: 'temp_' + Math.random().toString()}, {id: 'temp_' + Math.random().toString()}, {id: 'temp_' + Math.random().toString()}, {id: 'temp_' + Math.random().toString()}
  ].map(a => a || {});
}

export function insertNewZone(view: View, index: number) {
  if (!view.zones) {
    view.zones = [];
  }
  const zone: Zone = {
    id: getUUid(),
    articles: [],
    web_layout: 2,
    title: 'Untitled Zone ' + (view.zones.length + 1),
    source: 'articles'
  };

  const next = {...view};

  if(index === next.zones.length) {
    next.zones.push(zone);
  } else {
    next.zones.splice(index, 0, zone);
  }

  next.zones.forEach((zone, index) => zone.order = index + 1);

  return next;
}

export function getUUid() {
  return btoa((Date.now() + Math.random()).toString());
}


export function clampMaxArticles(view: View) {
  view.zones = view.zones.map(zone => {

    const maxArticles = getMaxArticlesForLayout(zone.web_layout);

    zone.articles = fillArticles(zone.articles || [], maxArticles)

    zone.articlePlaces = zone.articles.map(a => {
      if(zone.articleIds?.includes(a.id)) {
        return a.id;
      } else {
        return 'empty';
      }
    });

    zone.articleIds = zone.articlePlaces.filter(p => p !== 'empty');

    zone.count = getMaxArticlesForLayout(zone.web_layout);

    return {...zone};
  });

  return {...view};
}

export function fillArticles(articles: ArticleSchema[], maxArticles: number) {
  articles = articles || [];
  if(articles.length > maxArticles) {
    return articles.filter((a,i) => i < maxArticles)
  } else if(articles.length < maxArticles) {
    while(articles.length < maxArticles) {
      articles.push({id: 'temp_' + Math.random().toString()} as any);
    }
    return [...articles];
  }

  return [...articles];
}

export function getMaxArticlesForLayout(id: number) {
  if(id == 0 || id === 1) return 1;
  if(id == 2) return 2;
  if(id == 3) return 3;
  if(id == 4) return 3;
  if(id == 5) return 6;
  if(id == 6) return 6;
  if(id == 7) return 6;
  if(id == 8) return 6;
  if(id == 9) return 9;
  if(id == 10) return 12;

  return 12;
}